// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the pins' time series as a CSV file (owner, 2026-10-04: "give
 * the user the option to download the data tracked by those points").
 *
 * The same rules as the Analyze panel's exports (`analyzeExport.ts`):
 * RFC 4180, CRLF, quoted only when needed, and a header block that says
 * what the numbers are *of*, so a file read a week later still means
 * something.
 *
 *   dataset,Precipitation (IMERG NRT)
 *   units,mm/hr
 *   time,UTC
 *   exported_at,2026-10-04T15:12:00.000Z
 *   blank_cell,not read yet (the dataset has not played that frame)
 *   no_data,read: no measurement at that place
 *
 *   pin,name,lat,lon
 *   1,Pin 1,48.2,16.4
 *
 *   time,Pin 1,Pin 2
 *   2026-08-27T21:00:00.000Z,0.8,no_data
 *
 * One row per frame of the dataset's window, in order, unread frames
 * included as blank cells: a reader rebuilding the series has to see
 * where the gaps are, as the graph's rails show them.
 *
 * AYNI (owner, 2026-10-18): a pin following the highest or lowest value
 * was read at another place each frame; its place follows its values, in
 * two columns (`Highest lat`, `Highest lon`), and its row in the pin table
 * gives where it stands now.
 */

import { slotForTime, type MeasurementSeries } from '../services/measure/series'

type Cell = string | number | null | undefined

function cell(value: Cell): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function rows(list: readonly Cell[][]): string {
  return list.map((r) => r.map(cell).join(',')).join('\r\n')
}

export interface PinsCsvPin {
  readonly name: string
  readonly lat: number
  readonly lon: number
  readonly series: MeasurementSeries | null
}

export interface PinsCsvContext {
  readonly datasetTitle: string | null
  readonly units: string | undefined
  /** True when the series run in UTC dates; false for seconds of playback. */
  readonly dated: boolean
  /**
   * AYNI: a row's time as the frame it stands for, at the stream's
   * precision (`2003-08` on a monthly stream, `2075` on a yearly one;
   * frameCalendar.formatFrameIso). Absent or null: the full ISO instant.
   */
  readonly formatTime?: (ms: number) => string | null
  readonly exportedAt: Date
  /** AYNI: what the values are, when not the dataset's own ("change from the baseline held at 1950"). */
  readonly valuesNote?: string
  /** AYNI: the trend table (`pinTrendUI.trendCsvRows`), written after the values when trends are on. */
  readonly trends?: readonly (readonly Cell[])[]
  /** Where the data came from and how to reopen the view (`research/citation.ts`), after the header rows. */
  readonly provenance?: readonly (readonly Cell[])[]
  /**
   * AYNI: a period baseline's mean and σ at a pin for the frame a slot holds:
   * when given (and not null for the first pin read), two columns follow each pin.
   */
  readonly reference?: (pin: PinsCsvPin, timeMs: number) => { mean: number | null; sigma: number | null } | null
  /** AYNI (owner, 2026-10-18): the following pins looked on land only or at sea only. */
  readonly surface?: 'land' | 'sea'
}

/** The CSV text, or null when no pin has a series to write. */
export function buildPinsCsvText(pins: readonly PinsCsvPin[], ctx: PinsCsvContext): string | null {
  const axis = pins.find((p) => p.series)?.series
  if (!axis) return null
  const moving = pins.map((p) => !!p.series?.slots.some((slot) => slot.at))
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['units', ctx.units ?? ''],
    ...(ctx.valuesNote ? [['values', ctx.valuesNote] as Cell[]] : []),
    ['time', ctx.dated ? 'UTC' : 'seconds of playback'],
    ['exported_at', ctx.exportedAt.toISOString()],
    ['blank_cell', 'not read yet (the dataset has not played that frame)'],
    ['no_data', 'read: no measurement at that place'],
    ...(moving.some(Boolean) ? [['moving_pin', 'follows the highest or lowest value: read at another place each frame, given in its lat and lon columns'] as Cell[]] : []),
    ...(ctx.surface ? [['searched', `${ctx.surface === 'land' ? 'land only' : 'sea only'}: places at least 90 % ${ctx.surface}, lakes, rivers and the shore left out (ICGC land cover map and coastline of Catalonia, CC BY 4.0; © ESA WorldCover project 2021 / Contains modified Copernicus Sentinel data (2021) processed by ESA WorldCover consortium; Natural Earth elsewhere)`] as Cell[]] : []),
    ...(ctx.provenance ?? []).map((r) => [...r]),
    [],
    ['pin', 'name', 'lat', 'lon'],
    ...pins.map((p, i): Cell[] => [i + 1, p.name, Number(p.lat.toFixed(4)), Number(p.lon.toFixed(4))]),
    [],
    ['time', ...pins.flatMap((p, i) => [
      p.name,
      ...(ctx.reference ? [`${p.name} climatology_mean`, `${p.name} climatology_sigma`] : []),
      ...(moving[i] ? [`${p.name} lat`, `${p.name} lon`] : []),
    ])],
  ]
  const n = axis.slots.length
  const span = axis.endMs - axis.startMs
  const body: Cell[][] = []
  for (let i = 0; i < n; i++) {
    const ms = axis.startMs + (n > 1 ? (i / (n - 1)) * span : 0)
    const time = ctx.dated ? ctx.formatTime?.(ms) ?? new Date(ms).toISOString() : Number((ms / 1000).toFixed(2))
    body.push([time, ...pins.flatMap((p, k): Cell[] => {
      const at = p.series ? slotForTime(p.series, ms) : -1
      const slot = p.series && at >= 0 ? p.series.slots[at] : undefined
      // Four significant figures: finer than an 8-bit encoding resolves, without
      // the float noise of the decode (0.06896551724137945).
      const value: Cell = !slot?.sampled ? '' : slot.value === null ? 'no_data' : Number(slot.value.toPrecision(4))
      const where: Cell[] = !moving[k] ? [] : slot?.sampled && slot.at ? [Number(slot.at.lat.toFixed(4)), Number(slot.at.lon.toFixed(4))] : ['', '']
      if (!ctx.reference) return [value, ...where]
      // The climatology of the frame the slot holds (its own month), where it was read.
      const ref = slot?.sampled ? ctx.reference(slot.at ? { ...p, lat: slot.at.lat, lon: slot.at.lon } : p, slot.timeMs ?? ms) : null
      const fmt = (v: number | null | undefined): Cell => (v === null || v === undefined ? '' : Number(v.toPrecision(5)))
      return [value, fmt(ref?.mean), fmt(ref?.sigma), ...where]
    })])
  }
  const trends = ctx.trends?.length ? `\r\n${rows(ctx.trends.map((r) => [...r]))}\r\n` : ''
  return `${rows(head)}\r\n${rows(body)}\r\n${trends}`
}

/** AYNI (S8): what the pins' statistics file says at its top. */
export interface PinStatsCsvContext {
  readonly datasetTitle: string | null
  readonly units: string | undefined
  readonly exportedAt: Date
  readonly valuesNote?: string
  readonly provenance?: readonly (readonly Cell[])[]
}

/**
 * AYNI (S8): the trend, emergence and correlation tables alone
 * (`pinTrendUI`'s rows, as the pins' CSV carries them after the values),
 * under the same header, for the reproducibility bundle.
 */
export function buildPinStatsCsvText(tables: readonly (readonly Cell[])[], ctx: PinStatsCsvContext): string {
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['analysis', 'pin statistics (trend, time of emergence, correlation)'],
    ['units', ctx.units ?? ''],
    ...(ctx.valuesNote ? [['values', ctx.valuesNote] as Cell[]] : []),
    ['exported_at', ctx.exportedAt.toISOString()],
    ...(ctx.provenance ?? []).map((r) => [...r]),
    [],
  ]
  return `${rows(head)}\r\n${rows(tables.map((r) => [...r]))}\r\n`
}

/** Lower-case words joined by dashes, for a file name. */
function slugOf(text: string): string {
  return text.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
}

/**
 * AYNI: a CSV file name that says what it is, of what, and when, for every
 * download (pins, a line, region statistics): `ayni-stats-zone-tropics-imerg-2026-10-04.csv`.
 */
export function ayniCsvFilename(kind: string, datasetTitle: string | null, at: Date, detail?: string): string {
  const parts = ['ayni', kind, detail ? slugOf(detail) : '', slugOf(datasetTitle ?? '') || 'dataset', at.toISOString().slice(0, 10)]
  return `${parts.filter(Boolean).join('-')}.csv`
}

/** The pins' file: `ayni-pins-precipitation-imerg-nrt-2026-10-04.csv`. */
export function pinsCsvFilename(datasetTitle: string | null, at: Date): string {
  return ayniCsvFilename('pins', datasetTitle, at)
}
